<template>
  <div :class="$utilModalStyle.modalOverlay" @click="props.bgClickClose ? closeModal() : null">
    <div :class="[$utilModalStyle.modalContent, $style.calculationModalContent]">
      <h3 :class="[$utilModalStyle.modalTitle, $style.modalTitle]">
        {{ $t('calculation-criteria') }}
      </h3>

      <div :class="[$utilModalStyle.modalBody, $style.scrollBody]">
        <div :class="$style.note">
          ⚠️ {{ $t('participants-note') }}
        </div>

        <table :class="$style.table">
          <thead>
            <tr>
              <th>{{ $t('action') }}</th>
              <th>{{ $t('points') }}</th>
            </tr>
          </thead>

          <tbody>
            <!-- Overall formulas -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                🧮 {{ $t('score-formulas-section') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('player-score-formula') }}</td>
              <td>
                {{ $t('player-score-formula-detail') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('saboteur-score-formula') }}</td>
              <td>
                {{ $t('saboteur-score-formula-detail') }}
              </td>
            </tr>

            <!-- Preliminary -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                🗳️ {{ $t('prelim-section') }}
              </td>
            </tr>
            <tr>
              <td>🥇 {{ $t('prelim-vote-1-correct-text') }}</td>
              <td>+1 <span :class="$style.helperText">({{ $t('players-and-saboteur') }})</span></td>
            </tr>
            <tr>
              <td>🥈 {{ $t('prelim-vote-2-correct-text') }}</td>
              <td>+0.5 <span :class="$style.helperText">({{ $t('players-and-saboteur') }})</span></td>
            </tr>

            <!-- Player Final -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                ✅ {{ $t('final-section') }}
              </td>
            </tr>
            <tr>
              <td>📝 {{ $t('correct-text') }}</td>
              <td>+1 <span :class="$style.helperText">({{ $t('participating-players-only') }})</span></td>
            </tr>
            <tr>
              <td>🕵️ {{ $t('correct-saboteur') }}</td>
              <td>+1 <span :class="$style.helperText">({{ $t('participating-players-only') }})</span></td>
            </tr>

            <!-- Consensus -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                🤝 {{ $t('consensus-section') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('consensus-correct-text') }}</td>
              <td>
                {{ $t('correct-text-votes') }} / {{ $t('total-players') }}
                <div :class="$style.helperText">
                  {{ $t('consensus-detail') }}
                </div>
              </td>
            </tr>

            <!-- Winner -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                🏁 {{ $t('winner-section') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('winner-small-group') }}</td>
              <td>{{ $t('winner-small-group-detail') }}</td>
            </tr>
            <tr>
              <td>{{ $t('winner-large-group') }}</td>
              <td>{{ $t('winner-large-group-detail') }}</td>
            </tr>

            <!-- Saboteur -->
            <tr>
              <td colspan="2" :class="$style.sectionHeader">
                🎭 {{ $t('saboteur-section') }}
              </td>
            </tr>
            <tr>
              <td colspan="2" :class="$style.warningRow">
                ⚠️ {{ $t('saboteur-no-participation-rule') }}
              </td>
            </tr>
            <!-- Saboteur Confusion -->
            <tr>
              <td colspan="2" :class="$style.subSectionHeader">
                😵 {{ $t('saboteur-confusion-bonus') }}
                <div :class="$style.helperText">
                  {{ $t('wrong-text-votes') }} / {{ $t('total-players') }}
                </div>
              </td>
            </tr>
            <tr>
              <td>{{ $t('wrong-rate-less-25') }}</td>
              <td>+0</td>
            </tr>
            <tr>
              <td>{{ $t('wrong-rate-25-49') }}</td>
              <td>+0.25</td>
            </tr>
            <tr>
              <td>{{ $t('wrong-rate-50-74') }}</td>
              <td>+0.5</td>
            </tr>
            <tr>
              <td>{{ $t('wrong-rate-75-plus') }}</td>
              <td>+1</td>
            </tr>

            <!-- Saboteur Win -->
            <tr>
              <td colspan="2" :class="$style.subSectionHeader">
                🏆 {{ $t('saboteur-win-bonus') }}
              </td>
            </tr>
            <tr>
              <td>{{ $t('saboteur-wins-group') }}</td>
              <td>+2</td>
            </tr>

            <!-- Saboteur Participation -->
            <tr>
              <td colspan="2" :class="$style.subSectionHeader">
                💬 {{ $t('saboteur-participation-bonus') }}
                <div :class="$style.helperText">
                  {{ $t('saboteur-msgs') }} / {{ $t('avg-all-player-msgs') }}
                </div>
              </td>
            </tr>
            <tr>
              <td>{{ $t('ratio-less-25') }}</td>
              <td>+0</td>
            </tr>
            <tr>
              <td>{{ $t('ratio-25-49') }}</td>
              <td>+0.25</td>
            </tr>
            <tr>
              <td>{{ $t('ratio-50-74') }}</td>
              <td>+0.5</td>
            </tr>
            <tr>
              <td>{{ $t('ratio-75-99') }}</td>
              <td>+0.75</td>
            </tr>
            <tr>
              <td>{{ $t('ratio-100-plus') }}</td>
              <td>+1</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div :class="$utilModalStyle.modalActions">
        <button :class="$utilModalStyle.primaryAction" @click="props.closeModal">
          {{ $t('close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import styles from "@/css/CalculationModal.module.css";
import utilModalStyles from "@/css/UtilModal.module.css";
// import globalStyles from "@/css/global.module.css";

const $style = styles;
const $utilModalStyle = utilModalStyles;
// const $globalStyle = globalStyles;

// eslint-disable-next-line
const props = defineProps({
  closeModal: {
    type: Function,
    required: true,
  },
  bgClickClose: {
    type: Boolean,
    required: false,
    default: true,
  },
});
</script>

<style scoped></style>
